import {Component, Input, SimpleChanges, ViewChild} from "@angular/core";
import * as moment from "moment";
import {SessionCell} from "../../../../../models/session-cell.model";
import {MatTable} from "@angular/material/table";
import { CommonService } from "../../../services/common.service";
import {LogicAddButtonComponent} from "survey-creator-angular";

@Component({
  selector: 'app-session-scheduler',
  templateUrl: './session-scheduler.component.html',
  styleUrls: ['./session-scheduler.component.css']
})
export class SessionSchedulerComponent {
  @Input() startDateTime: string;
  @Input() disabled: boolean;
  @Input() resetSessionsList: boolean;
  @Input() previousSessionsList: any[];

  noOfWeeks = 30;
  maxWeeks = 52;
  weekdayList: string[] = [];
  dayList: SessionCell[][] = [];
  sessionList: SessionCell[] = [];
  maxSessions = 26;

  // For mat table
  displayedColumns: string[] = [];
  dataSource = [];
  @ViewChild(MatTable) table: MatTable<any>;

  constructor(private commonService: CommonService) {
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.table.updateStickyColumnStyles();
    }, 1000);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['previousSessionsList']?.currentValue?.length && !this.resetSessionsList) {
      this.sessionList = changes['previousSessionsList'].currentValue;
      if (!changes['startDateTime']?.currentValue) {
        this.startDateTime = this.sessionList[0].dateTime;
      }
      this.prepareDayGrid();
      this.patchValue(this.sessionList);
    }

    if (this.resetSessionsList) {
      this.renderDetails();
    }
  }

  renderDetails(noOfWeeks?:number) {
    this.resetConfigs(noOfWeeks); // reset all configs
    this.prepareDayGrid(); // re-render day list
  }

  resetConfigs(noOfWeeks?:number) {
    this.noOfWeeks = noOfWeeks || 30;
    this.maxWeeks = 52;
    this.weekdayList = [];
    this.dayList = [];
    this.sessionList = [];
    this.maxSessions = 26;
  }

  prepareDayGrid() {
    const matColumns = ['Week'];
    const matDataSource = [];

    const dateTimeMoment = this.startDateTime ? moment(this.startDateTime) : moment();

    // iterate for noOfWeeks given
    for (let weekIndex = 0; weekIndex < this.noOfWeeks; weekIndex++) {
      const daysOfWeek: SessionCell[] = [];
      const dataSourceItem = {};
      // iterate for 7 days
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const day: SessionCell = {
          dateTime: dateTimeMoment.toISOString(),
        }

        // if this is the first day of week then add as session
        if (dayIndex == 0 && this.sessionList.length < this.maxSessions) {
          this.sessionList.push(day)
          day.sessionNumber = this.sessionList.length; // here length works as increasing index
        }

        daysOfWeek.push(day);

        // collect week days here only
        if (this.weekdayList.length < 7) {
          this.weekdayList.push(dateTimeMoment.format('dddd'))
          matColumns.push(dateTimeMoment.format('dddd'));
        }
        dataSourceItem[this.weekdayList[dayIndex]] = day;

        // increase dateTime to next day
        dateTimeMoment.add(1, 'days')
      }

      // append week days to day list
      this.dayList.push(daysOfWeek);
      dataSourceItem['Week'] = weekIndex + 1;
      matDataSource.push(dataSourceItem);
    }


    this.displayedColumns = matColumns;
    this.dataSource = matDataSource;
  }

  onDaySelected(selectedDay: SessionCell) {
    if (this.disabled) {
      return
    }
    let hasChanged = false; // to avoid sort and renumbering when playing with table even when already added max sessions
    if (selectedDay.sessionNumber) { // unselect the session
      // remove the session from session list
      this.sessionList.splice(selectedDay.sessionNumber - 1, 1);

      // reset session number of selected day
      selectedDay.sessionNumber = null;

      hasChanged = true;
    } else if (this.sessionList.length < this.maxSessions) { // select the session
      // add selected day to session list
      this.sessionList.push(selectedDay)

      hasChanged = true;
    } else {
      this.commonService.notification('Maximum 26 sessions allowed', 'warning');
    }

    if (hasChanged) {
      // sort the sessions according the dates selected
      this.sessionList.sort((a, b) => moment(a.dateTime).diff(moment(b.dateTime)));

      // re-number the sessions
      this.sessionList.forEach((item, index) => {
        item.sessionNumber = index + 1;
      })
    }
  }

  addWeek() {
    if (this.noOfWeeks < this.maxWeeks) {
      this.noOfWeeks++; // increase week count

      const dateTimeMoment = moment(this.dayList[this.dayList.length - 1][6].dateTime); // get the last date from prepared day list
      const daysOfWeek: SessionCell[] = [];
      const dataSourceItem = {};
      // iterate for 7 days
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        // increase dateTime to next day
        dateTimeMoment.add(1, 'days')

        const day: SessionCell = {
          dateTime: dateTimeMoment.toISOString(),
        }

        daysOfWeek.push(day)
        dataSourceItem[this.weekdayList[dayIndex]] = day;
      }

      // append week days to day list
      this.dayList.push(daysOfWeek);
      dataSourceItem['Week'] = this.noOfWeeks;
      this.dataSource.push(dataSourceItem);
      this.dataSource = [...this.dataSource]
      setTimeout(() => {
        this.table.updateStickyColumnStyles();
      }, 10);
    }
  }

  patchValue(sessions: SessionCell[]) {
    if (!sessions?.length) {
      return;
    }
    const lastSessionDateTime = sessions[sessions.length - 1].dateTime;
    const firstSessionDateTime = sessions[0].dateTime;
    const momentLastDateTime = moment(lastSessionDateTime);
    const momentStartDateTime = moment(firstSessionDateTime);
    const diffWeeks = momentLastDateTime.diff(momentStartDateTime, 'weeks')
    if (diffWeeks > this.noOfWeeks) {
      this.noOfWeeks = diffWeeks + 1;
      this.renderDetails(this.noOfWeeks);
    }

    // clear existing session list
    this.sessionList.forEach((item) => {
      item.sessionNumber = null;
    })
    this.sessionList = [];

    for (let sessionIndex = 0; sessionIndex < sessions.length; sessionIndex++) {
      this.dataSource?.forEach((item) => {
        for (const itemKey in item) {
          const itemValue = item[itemKey]
          if (itemKey != 'Week' && itemValue.dateTime.split('T')[0] === sessions[sessionIndex].dateTime.split('T')[0]) {
            itemValue.sessionNumber = sessionIndex + 1;
            this.sessionList.push(itemValue);
          }
        }
      })
    }

    this.dataSource = [...this.dataSource];

    setTimeout(() => {
      this.table.updateStickyColumnStyles();
    }, 1000);
  }
}
